/**
 * Playlist Generator Controller
 *
 * Handles the UI for Suno AI playlist generation via the ShowLark extension.
 * Manages form state, extension status, generation progress, and playlist integration.
 *
 * Issue #7: Added advanced options for genre mixing, instrument selection,
 * vocal styles, style influences, and AI-powered lyric/prompt generation.
 */

import { elements } from './dom-cache.js';
import { generatorState, playlistState } from './state.js';
import { sunoService } from '../lib/suno/suno-service.js';
import { featureGate } from '../entitlements/feature-gate.js';
import { closeAllPanels } from './overlay-panels.js';
import { markDurationsLoadedIfReady } from './file-handler.js';
import { generateLyrics } from '../lib/suno/lyric-generator.js';
import { generatePrompt } from '../lib/suno/prompt-generator.js';

/**
 * Initialize the playlist generator
 */
export function initPlaylistGenerator() {
  setupExtensionStatus();
  setupFormControls();
  setupAdvancedOptions();
  setupGenerateButton();
  setupProgressControls();
  setupPushedSongsHandler();

  // Check extension status on load
  checkExtensionStatus();

  // Listen for extension ready event
  document.addEventListener('showlarkExtensionReady', () => {
    checkExtensionStatus();
  });

  // Listen for progress updates
  document.addEventListener('showlarkGenerationProgress', (event) => {
    handleProgressUpdate(event.detail.task);
  });

  console.log('[PlaylistGenerator] Initialized');
}

/**
 * Set up extension status display
 */
function setupExtensionStatus() {
  const extensionStatus = document.getElementById('extensionStatus');
  const installExtensionBtn = document.getElementById('installExtensionBtn');

  if (installExtensionBtn) {
    installExtensionBtn.addEventListener('click', () => {
      // Open Chrome Web Store or extension download page
      window.open('/extension', '_blank');
    });
  }
}

/**
 * Check extension availability and update UI
 */
async function checkExtensionStatus() {
  const extensionStatusCard = document.getElementById('extensionStatusCard');
  const extensionStatusText = document.getElementById('extensionStatusText');
  const extensionStatusIndicator = document.getElementById('extensionStatusIndicator');
  const installExtensionBtn = document.getElementById('installExtensionBtn');

  if (!extensionStatusCard) return;

  try {
    const available = await sunoService.isExtensionAvailable();

    if (available) {
      // Extension found - check token status
      // Note: We only check hasToken, not isValid. Suno uses short-lived Clerk JWTs
      // (1-5 min) that auto-rotate via ensureValidAccessToken() in the background.
      // The isValid flag reflects the stored expiresAt which is almost always stale
      // after any idle period. The background handler will silently refresh the token
      // when generation is actually attempted.
      const tokenStatus = await sunoService.getTokenStatus();

      if (tokenStatus.hasToken) {
        // Ready to generate
        extensionStatusCard.className = 'extension-status-card ready';
        extensionStatusIndicator?.classList.remove('warning', 'error');
        extensionStatusIndicator?.classList.add('valid');
        extensionStatusText.textContent = 'Connected to Suno';

        if (installExtensionBtn) installExtensionBtn.style.display = 'none';
        if (elements.generateBtn) elements.generateBtn.disabled = false;
      } else {
        // No token at all — user needs to log into Suno
        extensionStatusCard.className = 'extension-status-card warning';
        extensionStatusIndicator?.classList.remove('valid', 'error');
        extensionStatusIndicator?.classList.add('warning');
        extensionStatusText.textContent = 'Please log into Suno to enable generation';

        if (installExtensionBtn) {
          installExtensionBtn.textContent = 'Open Suno';
          installExtensionBtn.onclick = () => window.open('https://suno.com', '_blank');
          installExtensionBtn.style.display = 'inline-flex';
        }
        if (elements.generateBtn) elements.generateBtn.disabled = true;
      }

      generatorState.extensionAvailable = true;
    } else {
      // Extension not found
      extensionStatusCard.className = 'extension-status-card missing';
      extensionStatusIndicator?.classList.remove('valid', 'warning');
      extensionStatusIndicator?.classList.add('error');
      extensionStatusText.textContent = 'Extension required';

      if (installExtensionBtn) {
        installExtensionBtn.textContent = 'Install Extension';
        installExtensionBtn.onclick = () => window.open('/extension', '_blank');
        installExtensionBtn.style.display = 'inline-flex';
      }
      if (elements.generateBtn) elements.generateBtn.disabled = true;

      generatorState.extensionAvailable = false;
    }
  } catch (error) {
    console.error('[PlaylistGenerator] Error checking extension:', error);
    extensionStatusCard.className = 'extension-status-card missing';
    extensionStatusText.textContent = 'Extension not detected';
    if (elements.generateBtn) elements.generateBtn.disabled = true;
  }
}

/**
 * Set up form control handlers
 */
function setupFormControls() {
  // Song count control
  const songCountControl = document.getElementById('songCountControl');
  const customSongCountWrapper = document.getElementById('customSongCountWrapper');
  const customSongCountInput = document.getElementById('customSongCountInput');

  if (songCountControl) {
    songCountControl.querySelectorAll('.segment').forEach(btn => {
      btn.addEventListener('click', () => {
        songCountControl.querySelectorAll('.segment').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        const value = btn.dataset.value;

        // Handle custom option
        if (value === 'custom') {
          if (customSongCountWrapper) {
            customSongCountWrapper.style.display = 'block';
            if (customSongCountInput) {
              customSongCountInput.focus();
              // Use existing custom value or default
              const currentCustomValue = customSongCountInput.value;
              if (currentCustomValue && !isNaN(parseInt(currentCustomValue, 10))) {
                const numValue = parseInt(currentCustomValue, 10);
                generatorState.songCount = Math.max(1, Math.min(100, numValue));
                if (elements.generatorSongCount) {
                  elements.generatorSongCount.value = generatorState.songCount;
                }
              }
            }
          }
        } else {
          // Hide custom input for preset values
          if (customSongCountWrapper) {
            customSongCountWrapper.style.display = 'none';
          }
          const numValue = parseInt(value, 10);
          if (elements.generatorSongCount) {
            elements.generatorSongCount.value = numValue;
          }
          generatorState.songCount = numValue;
        }
      });
    });

    // Re-initialize Lucide icons for the custom segment
    if (window.lucide) {
      window.lucide.createIcons({ nodes: [songCountControl] });
    }
  }

  // Custom song count input handler
  if (customSongCountInput) {
    customSongCountInput.addEventListener('input', (e) => {
      let value = parseInt(e.target.value, 10);

      // Validate and clamp the value
      if (!isNaN(value)) {
        value = Math.max(1, Math.min(100, value));
        generatorState.songCount = value;
        if (elements.generatorSongCount) {
          elements.generatorSongCount.value = value;
        }
      }
    });

    // Ensure value is valid on blur
    customSongCountInput.addEventListener('blur', (e) => {
      let value = parseInt(e.target.value, 10);
      if (isNaN(value) || value < 1) {
        value = 1;
      } else if (value > 100) {
        value = 100;
      }
      e.target.value = value;
      generatorState.songCount = value;
      if (elements.generatorSongCount) {
        elements.generatorSongCount.value = value;
      }
    });
  }

  // Audio type control
  const audioTypeControl = document.getElementById('audioTypeControl');
  if (audioTypeControl) {
    audioTypeControl.querySelectorAll('.segment').forEach(btn => {
      btn.addEventListener('click', () => {
        audioTypeControl.querySelectorAll('.segment').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        const value = btn.dataset.value;
        if (elements.generatorAudioType) {
          elements.generatorAudioType.value = value;
        }
        generatorState.audioType = value;
      });
    });

    if (window.lucide) {
      window.lucide.createIcons({ nodes: [audioTypeControl] });
    }
  }

  // Genre input
  if (elements.generatorGenre) {
    elements.generatorGenre.addEventListener('input', (e) => {
      generatorState.genre = e.target.value;
    });
  }

  // Theme input
  if (elements.generatorTheme) {
    elements.generatorTheme.addEventListener('input', (e) => {
      generatorState.theme = e.target.value;
    });
  }

  // Fix prompt checkbox (Issue #10)
  if (elements.fixPromptCheckbox) {
    elements.fixPromptCheckbox.addEventListener('change', (e) => {
      generatorState.fixedPrompt = e.target.checked;
    });
  }
}

/**
 * Set up advanced options (Issue #7)
 */
function setupAdvancedOptions() {
  // Toggle advanced options panel
  if (elements.advancedToggleBtn) {
    elements.advancedToggleBtn.addEventListener('click', () => {
      const isExpanded = elements.advancedOptions?.style.display !== 'none';
      if (elements.advancedOptions) {
        elements.advancedOptions.style.display = isExpanded ? 'none' : 'block';
      }
      elements.advancedToggleBtn.classList.toggle('expanded', !isExpanded);
      generatorState.advancedMode = !isExpanded;

      if (window.lucide) {
        window.lucide.createIcons({ nodes: [elements.advancedToggleBtn] });
      }
    });
  }

  // Genre mix dropdown
  if (elements.genreMixDropdown) {
    elements.genreMixDropdown.addEventListener('change', (e) => {
      const value = e.target.value;
      if (value && !generatorState.genres.includes(value)) {
        generatorState.genres.push(value);
        renderSelectedGenres();
      }
      e.target.value = ''; // Reset dropdown
    });
  }

  // Mood selector
  if (elements.generatorMood) {
    elements.generatorMood.addEventListener('change', (e) => {
      generatorState.mood = e.target.value;
    });
  }

  // Instrument checkboxes
  if (elements.instrumentSelect) {
    elements.instrumentSelect.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.addEventListener('change', () => {
        const checked = elements.instrumentSelect.querySelectorAll('input[type="checkbox"]:checked');
        generatorState.instruments = Array.from(checked).map(cb => cb.value);
      });
    });
  }

  // Vocal options
  if (elements.vocalType) {
    elements.vocalType.addEventListener('change', (e) => {
      generatorState.vocals.type = e.target.value;
    });
  }
  if (elements.vocalStyle) {
    elements.vocalStyle.addEventListener('change', (e) => {
      generatorState.vocals.style = e.target.value;
    });
  }

  // Style influence input
  if (elements.styleInfluence) {
    elements.styleInfluence.addEventListener('input', (e) => {
      // Parse comma or + separated influences
      const value = e.target.value;
      generatorState.styleInfluences = value
        .split(/[,+]/)
        .map(s => s.trim())
        .filter(s => s.length > 0);
    });
  }

  // Era selector
  if (elements.generatorEra) {
    elements.generatorEra.addEventListener('change', (e) => {
      generatorState.era = e.target.value;
    });
  }

  // Generate AI prompt button
  if (elements.generatePromptBtn) {
    elements.generatePromptBtn.addEventListener('click', handleGeneratePrompt);
  }

  // Use generated prompt button
  if (elements.usePromptBtn) {
    elements.usePromptBtn.addEventListener('click', () => {
      if (generatorState.generatedPrompt && elements.generatorGenre) {
        elements.generatorGenre.value = generatorState.generatedPrompt;
        generatorState.genre = generatorState.generatedPrompt;
        if (elements.generatedPromptContainer) {
          elements.generatedPromptContainer.style.display = 'none';
        }
        showSuccess('Prompt applied to genre field');
      }
    });
  }

  // Generate lyrics checkbox
  if (elements.generateLyricsCheckbox) {
    elements.generateLyricsCheckbox.addEventListener('change', (e) => {
      generatorState.generateLyrics = e.target.checked;
      if (elements.lyricsForm) {
        elements.lyricsForm.style.display = e.target.checked ? 'block' : 'none';
      }
    });
  }

  // Lyrics theme input
  if (elements.lyricsTheme) {
    elements.lyricsTheme.addEventListener('input', (e) => {
      generatorState.lyricsTheme = e.target.value;
    });
  }

  // Lyrics mood selector
  if (elements.lyricsMood) {
    elements.lyricsMood.addEventListener('change', (e) => {
      generatorState.lyricsMood = e.target.value;
    });
  }

  // Lyrics structure selector
  if (elements.lyricsStructure) {
    elements.lyricsStructure.addEventListener('change', (e) => {
      generatorState.lyricsStructure = e.target.value;
    });
  }

  // Fix lyrics checkbox (Issue #10)
  if (elements.fixLyricsCheckbox) {
    elements.fixLyricsCheckbox.addEventListener('change', (e) => {
      generatorState.fixedLyrics = e.target.checked;
    });
  }

  // Generate lyrics button
  if (elements.generateLyricsBtn) {
    elements.generateLyricsBtn.addEventListener('click', handleGenerateLyrics);
  }

  // Use lyrics button
  if (elements.useLyricsBtn) {
    elements.useLyricsBtn.addEventListener('click', () => {
      if (generatorState.generatedLyrics) {
        // Lyrics will be used during playlist generation
        showSuccess('Lyrics saved for generation');
      }
    });
  }

  // Audio type change: show/hide vocal and lyrics options
  const audioTypeControl = document.getElementById('audioTypeControl');
  if (audioTypeControl) {
    audioTypeControl.querySelectorAll('.segment').forEach(btn => {
      btn.addEventListener('click', () => {
        const isVocal = btn.dataset.value === 'vocal';
        if (elements.vocalOptions) {
          elements.vocalOptions.style.display = isVocal ? 'block' : 'none';
        }
        if (elements.lyricsOptions) {
          elements.lyricsOptions.style.display = isVocal ? 'block' : 'none';
        }
      });
    });
  }
}

/**
 * Render selected genres as tags
 */
function renderSelectedGenres() {
  if (!elements.selectedGenres) return;

  elements.selectedGenres.innerHTML = generatorState.genres.map(genre => `
    <span class="selected-tag" data-genre="${genre}">
      ${formatGenreName(genre)}
      <button class="remove-tag" type="button" aria-label="Remove ${genre}">
        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
      </button>
    </span>
  `).join('');

  // Add remove handlers
  elements.selectedGenres.querySelectorAll('.remove-tag').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const tag = e.target.closest('.selected-tag');
      const genre = tag?.dataset.genre;
      if (genre) {
        generatorState.genres = generatorState.genres.filter(g => g !== genre);
        renderSelectedGenres();
      }
    });
  });
}

/**
 * Format genre name for display
 */
function formatGenreName(genre) {
  return genre
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Handle generate AI prompt button click
 */
async function handleGeneratePrompt() {
  // Validate: need at least genres or basic genre input
  const hasGenres = generatorState.genres.length > 0;
  const hasBasicGenre = generatorState.genre?.trim().length > 0;

  if (!hasGenres && !hasBasicGenre) {
    showError('Please select at least one genre or enter a genre/style');
    return;
  }

  // Check feature access
  const allowed = await featureGate.checkFeature('generate_playlists');
  if (!allowed) return;

  try {
    generatorState.isGeneratingContent = true;
    elements.generatePromptBtn?.classList.add('loading');

    // Prepare genres array - combine selected and basic genre
    const genres = [...generatorState.genres];
    if (hasBasicGenre && !genres.includes(generatorState.genre)) {
      genres.unshift(generatorState.genre.trim());
    }

    const result = await generatePrompt({
      genres,
      mood: generatorState.mood || undefined,
      instruments: generatorState.instruments.length > 0 ? generatorState.instruments : undefined,
      vocals: (generatorState.vocals.type || generatorState.vocals.style) ? generatorState.vocals : undefined,
      styleInfluences: generatorState.styleInfluences.length > 0 ? generatorState.styleInfluences : undefined,
      era: generatorState.era || undefined
    });

    generatorState.generatedPrompt = result.prompt;

    // Show generated prompt
    if (elements.generatedPrompt) {
      elements.generatedPrompt.value = result.prompt;
    }
    if (elements.generatedPromptContainer) {
      elements.generatedPromptContainer.style.display = 'block';
    }

    showSuccess('Prompt generated!');

  } catch (error) {
    console.error('[PlaylistGenerator] Prompt generation error:', error);
    showError(error.message || 'Failed to generate prompt');
  } finally {
    generatorState.isGeneratingContent = false;
    elements.generatePromptBtn?.classList.remove('loading');
  }
}

/**
 * Handle generate lyrics button click
 */
async function handleGenerateLyrics() {
  const theme = generatorState.lyricsTheme?.trim();
  if (!theme) {
    showError('Please enter a theme for the lyrics');
    elements.lyricsTheme?.focus();
    return;
  }

  // Check feature access
  const allowed = await featureGate.checkFeature('generate_playlists');
  if (!allowed) return;

  try {
    generatorState.isGeneratingContent = true;
    elements.generateLyricsBtn?.classList.add('loading');

    const result = await generateLyrics({
      theme,
      mood: generatorState.lyricsMood || undefined,
      genre: generatorState.genre || undefined,
      structure: generatorState.lyricsStructure || 'verse-chorus'
    });

    generatorState.generatedLyrics = result.lyrics;

    // Show generated lyrics
    if (elements.generatedLyrics) {
      elements.generatedLyrics.value = result.lyrics;
    }
    if (elements.generatedLyricsContainer) {
      elements.generatedLyricsContainer.style.display = 'block';
    }

    showSuccess('Lyrics generated!');

  } catch (error) {
    console.error('[PlaylistGenerator] Lyrics generation error:', error);
    showError(error.message || 'Failed to generate lyrics');
  } finally {
    generatorState.isGeneratingContent = false;
    elements.generateLyricsBtn?.classList.remove('loading');
  }
}

/**
 * Set up generate button handler
 */
function setupGenerateButton() {
  if (!elements.generateBtn) return;
  elements.generateBtn.addEventListener('click', handleGenerate);
}

/**
 * Handle generate button click
 */
async function handleGenerate() {
  // Check feature access (premium only)
  const allowed = await featureGate.checkFeature('generate_playlists');
  if (!allowed) {
    return;
  }

  // Validate inputs
  const genre = elements.generatorGenre?.value.trim();
  if (!genre) {
    showError('Please enter a genre or style');
    elements.generatorGenre?.focus();
    return;
  }

  // Get form values
  const theme = elements.generatorTheme?.value.trim() || '';
  const songCount = generatorState.songCount;
  const audioType = generatorState.audioType;

  // Notify Suno Manager to close if open in expanded mode
  document.dispatchEvent(new CustomEvent('playlistGenerationStarted'));

  // Show progress UI
  showProgressUI(songCount);

  try {
    generatorState.isGenerating = true;
    elements.generateBtn?.classList.add('loading');

    // Set up progress listener
    sunoService.onProgress(handleProgressUpdate);

    // Start generation via extension
    // Pass fixedPrompt/fixedLyrics from state (Issue #10)
    const result = await sunoService.generatePlaylist({
      genre,
      theme,
      songCount,
      audioType,
      fixedPrompt: generatorState.fixedPrompt,
      fixedLyrics: generatorState.fixedLyrics
    });

    generatorState.currentTask = {
      id: result.taskId,
      status: result.status,
      progress: 0,
      songs: []
    };

    // Progress updates come via events from extension

  } catch (error) {
    console.error('[PlaylistGenerator] Generation error:', error);
    showError(error.message);
    resetUI();
  }
}

/**
 * Show progress UI
 */
function showProgressUI(songCount) {
  if (elements.generatorForm) {
    elements.generatorForm.style.display = 'none';
  }
  if (elements.generatorProgress) {
    elements.generatorProgress.style.display = 'block';
  }
  if (elements.generatorCaptcha) {
    elements.generatorCaptcha.style.display = 'none';
  }

  // Initialize song list
  if (elements.generatorSongList) {
    elements.generatorSongList.innerHTML = '';
    for (let i = 0; i < songCount; i++) {
      const li = document.createElement('li');
      li.className = 'generator-song-item';
      li.innerHTML = `
        <span class="generator-song-status pending">
          <i data-lucide="circle"></i>
        </span>
        <span class="generator-song-title pending">Waiting...</span>
      `;
      elements.generatorSongList.appendChild(li);
    }

    if (window.lucide) {
      window.lucide.createIcons({ nodes: [elements.generatorSongList] });
    }
  }

  updateProgress(0);
}

/**
 * Handle progress update from extension
 */
function handleProgressUpdate(task) {
  if (!task) return;

  generatorState.currentTask = task;

  // Handle different statuses
  if (task.status === 'captcha_required') {
    showCaptchaUI(task);
    return;
  }

  if (task.status === 'complete') {
    handleGenerationComplete(task);
    return;
  }

  if (task.status === 'error') {
    showError(task.error || 'Generation failed');
    resetUI();
    return;
  }

  if (task.status === 'cancelled') {
    resetUI();
    return;
  }

  // Update progress display
  updateProgress(task.progress);

  // Update song list
  if (elements.generatorSongList && task.songs) {
    const items = elements.generatorSongList.querySelectorAll('.generator-song-item');

    task.songs.forEach((song, index) => {
      // Find the item by song index
      const itemIndex = song.index !== undefined ? song.index : index;
      if (items[itemIndex]) {
        const statusEl = items[itemIndex].querySelector('.generator-song-status');
        const titleEl = items[itemIndex].querySelector('.generator-song-title');

        if (song.status === 'complete') {
          statusEl.className = 'generator-song-status complete';
          statusEl.innerHTML = '<i data-lucide="check-circle"></i>';
          titleEl.className = 'generator-song-title';
          titleEl.textContent = song.title || 'Untitled';

          // Add download button if not present
          if (!items[itemIndex].querySelector('.generator-song-download')) {
            const downloadBtn = document.createElement('button');
            downloadBtn.className = 'generator-song-download';
            downloadBtn.innerHTML = '<i data-lucide="download"></i>';
            downloadBtn.title = 'Download';
            downloadBtn.onclick = () => downloadSong(song);
            items[itemIndex].appendChild(downloadBtn);
          }
        } else if (song.status === 'error') {
          statusEl.className = 'generator-song-status error';
          statusEl.innerHTML = '<i data-lucide="x-circle"></i>';
          titleEl.className = 'generator-song-title';
          titleEl.textContent = song.error || 'Failed';
        } else if (song.status === 'generating' || song.status === 'streaming') {
          statusEl.className = 'generator-song-status generating';
          statusEl.innerHTML = '<i data-lucide="loader-2"></i>';
          titleEl.className = 'generator-song-title';
          titleEl.textContent = 'Generating...';
        }
      }
    });

    if (window.lucide) {
      window.lucide.createIcons({ nodes: [elements.generatorSongList] });
    }
  }
}

/**
 * Update progress bar
 */
function updateProgress(percent) {
  if (elements.generatorProgressBar) {
    elements.generatorProgressBar.style.width = `${percent}%`;
  }
  if (elements.generatorProgressText) {
    elements.generatorProgressText.textContent = `${Math.round(percent)}%`;
  }
}

/**
 * Show CAPTCHA UI
 */
function showCaptchaUI(task) {
  if (elements.generatorProgress) {
    elements.generatorProgress.style.display = 'none';
  }
  if (elements.generatorCaptcha) {
    elements.generatorCaptcha.style.display = 'block';
  }

  generatorState.captchaRequired = true;
  generatorState.captchaData = task.captchaData;
}

/**
 * Set up progress controls
 */
function setupProgressControls() {
  // Cancel button
  if (elements.cancelGenerationBtn) {
    elements.cancelGenerationBtn.addEventListener('click', async () => {
      try {
        await sunoService.cancelGeneration();
        sunoService.clearProgress();
        resetUI();
      } catch (error) {
        console.error('[PlaylistGenerator] Cancel error:', error);
      }
    });
  }

  // Open Suno button (for CAPTCHA)
  if (elements.openSunoBtn) {
    elements.openSunoBtn.addEventListener('click', () => {
      window.open('https://suno.com/create', '_blank');
    });
  }

  // Resume button
  if (elements.resumeGenerationBtn) {
    elements.resumeGenerationBtn.addEventListener('click', handleResume);
  }
}

/**
 * Handle resume after CAPTCHA
 */
async function handleResume() {
  if (!generatorState.currentTask?.id) return;

  try {
    elements.resumeGenerationBtn?.classList.add('loading');

    // Resume via extension
    await sunoService.resumeAfterCaptcha();

    // Show progress UI again
    if (elements.generatorCaptcha) {
      elements.generatorCaptcha.style.display = 'none';
    }
    if (elements.generatorProgress) {
      elements.generatorProgress.style.display = 'block';
    }

    generatorState.captchaRequired = false;

    // Progress updates come via events

  } catch (error) {
    showError(error.message);
  } finally {
    elements.resumeGenerationBtn?.classList.remove('loading');
  }
}

/**
 * Handle generation complete
 */
async function handleGenerationComplete(task) {
  generatorState.isGenerating = false;
  elements.generateBtn?.classList.remove('loading');
  sunoService.clearProgress();

  const completedSongs = task.songs.filter(s => s.status === 'complete');

  if (completedSongs.length === 0) {
    showError('No songs were generated successfully');
    resetUI();
    return;
  }

  // Add songs to playlist
  for (const song of completedSongs) {
    addSongToPlaylist(song);
  }

  // Dispatch event to notify other components (e.g., library browser)
  document.dispatchEvent(new CustomEvent('showlarkGenerationComplete', {
    detail: {
      songCount: completedSongs.length,
      songs: completedSongs
    }
  }));

  // Show success message
  showSuccess(`Added ${completedSongs.length} songs to playlist`);

  // Reset UI after delay
  setTimeout(() => {
    resetUI();
    closeAllPanels();
  }, 2000);
}

/**
 * Handle songs pushed from extension popup
 */
function setupPushedSongsHandler() {
  document.addEventListener('playlistSongsReceived', (event) => {
    const { songs, source } = event.detail;

    if (songs && songs.length > 0) {
      for (const song of songs) {
        addSongToPlaylist(song);
      }
      showSuccess(`Added ${songs.length} songs from extension`);
    }
  });
}

/**
 * Add a generated song to the playlist
 */
function addSongToPlaylist(song) {
  const playlistEntry = {
    file: null,
    name: song.title || 'Untitled',
    included: true,
    duration: song.duration || 0,
    audioBuffer: null,
    lyrics: null,
    lyricsFile: null,
    error: false,
    errorMessage: null,
    addedAt: Date.now(),
    // Suno-specific properties
    isStreamed: true,
    streamUrl: song.audioUrl,
    imageUrl: song.imageUrl,
    sunoClipId: song.id
  };

  playlistState.playlist.push(playlistEntry);

  // Generated songs arrive with durations from API — mark as loaded
  markDurationsLoadedIfReady();

  document.dispatchEvent(new CustomEvent('playlistUpdated', {
    detail: { source: 'generator' }
  }));
}

/**
 * Download a song
 */
async function downloadSong(song) {
  if (!song.audioUrl) return;

  try {
    const response = await fetch(song.audioUrl);
    const blob = await response.blob();

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${song.title || 'song'}.mp3`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Download failed:', error);
    showError('Failed to download song');
  }
}

/**
 * Reset UI to initial state
 */
function resetUI() {
  if (elements.generatorForm) {
    elements.generatorForm.style.display = 'block';
  }
  if (elements.generatorProgress) {
    elements.generatorProgress.style.display = 'none';
  }
  if (elements.generatorCaptcha) {
    elements.generatorCaptcha.style.display = 'none';
  }

  generatorState.isGenerating = false;
  generatorState.currentTask = null;
  generatorState.captchaRequired = false;
  generatorState.captchaData = null;
  generatorState.error = null;

  elements.generateBtn?.classList.remove('loading');
  sunoService.clearProgress();

  // Re-check extension status
  checkExtensionStatus();
}

/**
 * Show error message
 */
function showError(message) {
  if (window.showToast) {
    window.showToast(message, 'error');
  } else {
    console.error('[PlaylistGenerator]', message);
    alert(message);
  }

  generatorState.error = message;
}

/**
 * Show success message
 */
function showSuccess(message) {
  if (window.showToast) {
    window.showToast(message, 'success');
  } else {
    console.log('[PlaylistGenerator]', message);
  }
}
